import { memo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import useSWR from "swr";

import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Breadcrumb from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { useConnectionStore } from "stores/ConnectionStore";
import { useFlairStore } from "stores/FlairStore";
import {
  ActionState,
  ProvisionState,
  SecretState,
  swrFetcher,
} from "stores/lib";

import { captureSentryException } from "utility/capture_sentry_exception";

import LoaderModal from "layouts/components/LoaderModal";

import ServiceConnectionInstructions from "components/ServiceConnectionInstructions";
import ServiceConnectionStep from "components/ServiceConnectionStep";

import Error from "./Error";
import CheckingOrganizationalLevelAccessPhase from "./Phase/CheckingOrganizationalLevelAccess";
import CheckingScopesPhase from "./Phase/CheckingScopes";
import CompletedPhase from "./Phase/Completed";
import EnablingRequisiteScopesPhase from "./Phase/EnablingRequisiteScopes";

enum Phase {
  EnterDetails,
  EnterError,
  SecretExists,
  DeleteError,
  Provisioning,
  Error,
}

enum ProvisionPhase {
  CheckingScopes,
  CheckingOrganizationalLevelAccess,
  EnablingRequisiteScopes,
  Done,
}

const ProvisionURL = "api/organization/gcp/provision";

const GcpConnectSetupPage = memo(function GcpConnectSetupPage() {
  const [connectionStore, connectionActions] = useConnectionStore();
  const [, flairActions] = useFlairStore();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [phase, setPhase] = useState(Phase.EnterDetails);
  const [projectId, setProjectId] = useState("");
  const [confirmSa, setConfirmSa] = useState(false);
  const [deleteSecretLoading, setDeleteSecretLoading] = useState(false);

  const [uuid, setUuid] = useState("");
  const [provisionPhase, setProvisionPhase] = useState(
    ProvisionPhase.CheckingScopes
  );
  const [refreshInterval, setRefreshInterval] = useState(2000);
  const [retryLoading, setRetryLoading] = useState(false);

  useSWR<ProvisionStateResponse>(
    ProvisionURL,
    phase === Phase.Provisioning
      ? async (url) =>
          await swrFetcher(url, {
            method: "POST",
            body: JSON.stringify({
              uuid,
            }),
          })
      : null,
    {
      refreshInterval,
      onError: (error) => {
        captureSentryException(
          error,
          "GCP connect error provisioning/polling for provision status"
        );
        setRefreshInterval(0);
        setPhase(Phase.Error);

        //
        const { info = { Unknown: "An error occurred" } } = error;
        const message = Object.keys(info).map((key) => (
          <Typography variant="body1" key={key}>
            <strong>{key}:</strong>
            {info[key]}
          </Typography>
        ));

        //
        toast.error("There was an error connecting to Google Cloud Platform", {
          position: "bottom-center",
        });
        setErrorMessage(
          (
            <Stack spacing={1} direction="column">
              {message}
            </Stack>
          ) as any
        );
      },

      onSuccess: (data) => {
        const provisionState = data?.state;
        if (provisionState !== undefined) {
          switch (provisionState) {
            case ProvisionState.QUEUED:
              setProvisionPhase(
                ProvisionPhase.CheckingOrganizationalLevelAccess
              );
              break;

            case ProvisionState.RUNNING:
              setProvisionPhase(ProvisionPhase.EnablingRequisiteScopes);
              break;

            case ProvisionState.COMPLETED:
              // celebration on first connection
              if (connectionStore.connections.size < 1) {
                flairActions.startCelebration();
              }

              toast.success("Successfully connected to Google Cloud Platform", {
                position: "bottom-center",
              });

              setRefreshInterval(0);
              setProvisionPhase(ProvisionPhase.Done);
              break;
          }
        }
      },
    }
  );

  const connect = () => {
    setLoading(true);

    (async () => {
      connectionActions.createConnection({
        kind: "gcp_resource_id",
        data: { gcp_resource_id: projectId, is_org_level: false },
        onSuccess,
        onError,
      });
    })();
  };

  const reset = () => {
    setProjectId("");
    setConfirmSa(false);
  };

  const onSuccess = (data: any) => {
    setLoading(false);
    setPhase(Phase.Provisioning);
    setUuid(data.uuid);
    connectionActions.loadConnections({ reload: true });
  };

  const onError = (error: any) => {
    if (
      error &&
      error.message === "secret of this kind already exists in the organization"
    ) {
      setPhase(Phase.SecretExists);
      setLoading(false);
      return;
    }

    setErrorMessage(
      (
        <Stack spacing={1} direction="column">
          {error?.message || "There was an error"}
        </Stack>
      ) as any
    );
    setPhase(Phase.EnterError);
    setLoading(false);
  };

  const deleteSecret = () => {
    setDeleteSecretLoading(true);
    (async () => {
      connectionActions.deleteConnection({
        uuid,
        onSuccess: onDeleteSuccess,
        onError: onDeleteError,
      });
    })();
  };

  const onDeleteSuccess = () => {
    setDeleteSecretLoading(false);
    toast.success("Successfully removed old Google Cloud Platform connection", {
      position: "bottom-center",
    });
    reset();
    setPhase(Phase.EnterDetails);
  };

  const onDeleteError = () => {
    setDeleteSecretLoading(false);
    setPhase(Phase.DeleteError);
  };

  const handleRetry = async () => {
    setRetryLoading(true);
    await connectionActions.deleteConnection({
      uuid,
      onSuccess: onRetrySuccess,
      onError: onRetryError,
    });
  };

  const onRetrySuccess = () => {
    setRetryLoading(false);
    setErrorMessage(undefined as any);
    reset();
    setProvisionPhase(ProvisionPhase.CheckingScopes);
    setPhase(Phase.EnterDetails);
  };

  const onRetryError = () => {
    setRetryLoading(false);
  };

  const handleEnterRetry = () => {
    setErrorMessage(undefined as any);
    reset();
    setPhase(Phase.EnterDetails);
  };

  if (connectionStore.fetchPublicKey.state === ActionState.INITIAL) {
    connectionActions.fetchPublicKey({ force: false });
    return <LoaderModal />;
  }

  return (
    <>
      <Breadcrumbs>
        <Breadcrumb
          underline="none"
          color="inherit"
          component={Link}
          to="/environment/sources"
        >
          Environment
        </Breadcrumb>
        <Breadcrumb
          underline="none"
          color="inherit"
          component={Link}
          to="/environment/sources"
        >
          Sources
        </Breadcrumb>
        <span />
      </Breadcrumbs>

      <ServiceConnectionInstructions
        label="Google Cloud Connection"
        forceCollapse={
          provisionPhase === ProvisionPhase.Done ? new Date() : undefined
        }
      >
        <Stack spacing={2}>
          <Typography variant="body1" fontWeight="bold">
            How the ThreatKey for Google Cloud connection works:
          </Typography>
          <Typography variant="body1">
            Connect your ThreatKey account with Google Cloud Platform in a just
            a few easy steps. By connecting Google Cloud Platform to ThreatKey,
            you can get a comprehensive view of your Google Cloud
            environment&apos;s security posture, identify and prioritize risks,
            and remediate them quickly and easily.
          </Typography>
          <Typography variant="body1">
            <strong>
              In order for the ThreatKey for Google Cloud connection to work,
              you will need to do the following:
            </strong>
            <ol>
              <li>
                Grant the ThreatKey Service Account access to your project by
                navigating{" "}
                <a href="https://console.cloud.google.com/iam-admin/iam">
                  here
                </a>{" "}
                and clicking &quot;Grant Access&quot;. The principal should be:
                <ul>
                  <li>
                    <code>
                      threatkey@gcp-scanner-prod.iam.gserviceaccount.com
                    </code>
                  </li>
                </ul>
              </li>
              <li>
                Grant the following roles to the above service account
                <ul>
                  <li>
                    Service Usage Admin (
                    <code>roles/serviceusage.serviceUsageAdmin</code>)
                  </li>
                  <li>
                    Viewer (<code>roles/viewer</code>)
                  </li>
                  <li>
                    Security Reviewer (<code>roles/iam.securityReviewer</code>)
                  </li>
                  <li>
                    StackDriver Accounts Viewer (
                    <code>roles/stackdriver.accounts.viewer</code>)
                  </li>
                </ul>
              </li>
            </ol>
          </Typography>
        </Stack>
      </ServiceConnectionInstructions>

      <Stack spacing={2} direction="column">
        <ServiceConnectionStep
          disabled={loading || phase !== Phase.EnterDetails}
        >
          <Stack spacing={1.5} direction="column">
            <Typography variant="body1">
              Please provide your <strong>Google Cloud Project ID</strong>.
            </Typography>
            <TextField
              fullWidth
              label="Google Cloud Project ID"
              placeholder="your-gcp-project-id"
              value={projectId}
              onChange={(event) => {
                setProjectId(event.target.value);
              }}
              data-private="lipsum"
            />

            <FormGroup>
              <Typography variant="body1">
                Please ensure that you have added the{" "}
                <strong>ThreatKey Service Account</strong> to your project in
                Google Cloud.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmSa}
                    onChange={() => setConfirmSa(!confirmSa)}
                  />
                }
                label={
                  <Typography variant="body1">
                    I have added the <strong>ThreatKey Service Account</strong>{" "}
                    to my project in Google Cloud Platform.
                  </Typography>
                }
              />
            </FormGroup>
            <Stack sx={{ mt: 2 }} spacing={2} direction="row">
              <Tooltip
                arrow
                title={
                  !projectId || !confirmSa ? "Please fill out the form" : ""
                }
              >
                <span>
                  <LoadingButton
                    variant="contained"
                    onClick={connect}
                    loading={loading}
                    disabled={!projectId || !confirmSa}
                  >
                    Connect to Google Cloud Platform
                  </LoadingButton>
                </span>
              </Tooltip>
              <Button variant="text" onClick={reset}>
                Clear
              </Button>
            </Stack>
          </Stack>
        </ServiceConnectionStep>

        {phase === Phase.SecretExists ? (
          <Alert severity="error">
            A connection to Google Cloud already exists in the organization. You
            can remove the old connection and try again.
            <LoadingButton
              variant="contained"
              color="error"
              sx={{ mt: 2 }}
              onClick={deleteSecret}
              loading={deleteSecretLoading}
            >
              Remove old Google Cloud connection
            </LoadingButton>
          </Alert>
        ) : null}

        {phase === Phase.DeleteError ? (
          <Alert severity="error">
            There was an error deleting your old Google Cloud connection. Please{" "}
            <Link to="/environment/sources">return to your sources</Link>.
          </Alert>
        ) : null}

        <Error
          show={phase === Phase.EnterError}
          loading={false}
          onTryAgain={handleEnterRetry}
        >
          {errorMessage}
        </Error>

        {phase >= Phase.Provisioning ? (
          <>
            <CheckingScopesPhase
              disabled={provisionPhase !== ProvisionPhase.CheckingScopes}
              progressIcon={
                provisionPhase === ProvisionPhase.CheckingScopes &&
                phase !== Phase.Error
              }
              successIcon={
                provisionPhase > ProvisionPhase.CheckingScopes &&
                phase !== Phase.Error
              }
              errorIcon={
                provisionPhase === ProvisionPhase.CheckingScopes &&
                phase === Phase.Error
              }
            />

            <Error
              show={
                provisionPhase === ProvisionPhase.CheckingScopes &&
                phase === Phase.Error
              }
              loading={retryLoading}
              onTryAgain={handleRetry}
            >
              {errorMessage}
            </Error>

            <CheckingOrganizationalLevelAccessPhase
              disabled={
                provisionPhase !==
                ProvisionPhase.CheckingOrganizationalLevelAccess
              }
              progressIcon={
                provisionPhase ===
                  ProvisionPhase.CheckingOrganizationalLevelAccess &&
                phase !== Phase.Error
              }
              successIcon={
                provisionPhase >
                  ProvisionPhase.CheckingOrganizationalLevelAccess &&
                phase !== Phase.Error
              }
              errorIcon={
                provisionPhase ===
                  ProvisionPhase.CheckingOrganizationalLevelAccess &&
                phase === Phase.Error
              }
            />

            <Error
              show={
                provisionPhase ===
                  ProvisionPhase.CheckingOrganizationalLevelAccess &&
                phase === Phase.Error
              }
              loading={retryLoading}
              onTryAgain={handleRetry}
            >
              {errorMessage}
            </Error>

            <EnablingRequisiteScopesPhase
              disabled={
                provisionPhase !== ProvisionPhase.EnablingRequisiteScopes
              }
              progressIcon={
                provisionPhase === ProvisionPhase.EnablingRequisiteScopes &&
                phase !== Phase.Error
              }
              successIcon={
                provisionPhase > ProvisionPhase.EnablingRequisiteScopes &&
                phase !== Phase.Error
              }
              errorIcon={
                provisionPhase === ProvisionPhase.EnablingRequisiteScopes &&
                phase === Phase.Error
              }
            />

            <Error
              show={
                provisionPhase === ProvisionPhase.EnablingRequisiteScopes &&
                phase === Phase.Error
              }
              loading={retryLoading}
              onTryAgain={handleRetry}
            >
              {errorMessage}
            </Error>

            <CompletedPhase
              disabled={provisionPhase !== ProvisionPhase.Done}
              successIcon={provisionPhase === ProvisionPhase.Done}
              onClickFn={() => {
                connectionActions.loadConnections({ reload: true });
              }}
            />
          </>
        ) : null}
      </Stack>
    </>
  );
});

export default GcpConnectSetupPage;

interface ProvisionStateResponse {
  state: ProvisionState;
  secret_state?: SecretState;
  secret_details?: string;
  details: any;
}
